import { apiClient } from './apiClient';

export type ManagementSearchType = '0' | '1' | '2' | '3' | '4' | '5';
export type PackageFilterStatus = 'active' | 'declined' | 'inactive';

export interface SelectValueOption {
  value: number;
  label: string;
}

export interface PackageManagementOptions {
  templates: SelectValueOption[];
  facilities: SelectValueOption[];
}

export interface PackageManagementFilters {
  page: number;
  perPage: number;
  sortBy: string;
  sortDir: 'asc' | 'desc';
  search?: string;
  searchType?: ManagementSearchType;
  dateFrom?: string;
  dateTo?: string;
  templateId?: number;
  templateIds?: number[];
  templateIdsNegate?: boolean;
  status?: PackageFilterStatus;
  statuses?: PackageFilterStatus[];
  statusesNegate?: boolean;
  facilityId?: number;
  facilityIds?: number[];
  facilityIdsNegate?: boolean;
  export?: boolean;
}

export interface PackageManagementRow {
  patientPackageId: number;
  packageId: number;
  patientId: number;
  paymentId: number | null;
  planId: number | null;
  patientName: string;
  type: string;
  amount: number;
  status: string;
  /** Units the guest can still use, by the Remaining Products filter's rule (SCRUM-2371). */
  productsRemaining: number;
  notes: string | null;
  facilityId: number | null;
  userName: string | null;
  createdAt: string | null;
}

export interface PackageManagementStats {
  activeCount: number;
  declinedCount: number;
  inactiveCount: number;
}

export interface PaginatedResult<T, TStats> {
  items: T[];
  pagination: {
    page: number;
    perPage: number;
    total: number;
  };
  sort: {
    field: string;
    direction: 'asc' | 'desc';
  };
  stats: TStats;
}

function asNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
    return Number(value);
  }
  return fallback;
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function isZeroDateString(value: string): boolean {
  return value === '0000-00-00' || value === '0000-00-00 00:00:00';
}

function asNullableString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' && !isZeroDateString(value.trim()) ? value : null;
}

function cleanParams(filters: Partial<PackageManagementFilters>) {
  const entries = Object.entries(filters).filter(([, value]) => {
    if (value == null) return false;
    if (typeof value === 'string') return value.trim() !== '';
    return true;
  });

  return Object.fromEntries(entries);
}

export async function getPackageManagementOptions(): Promise<PackageManagementOptions> {
  const res = await apiClient.get<{ data: { templates?: unknown[]; facilities?: unknown[] } }>(
    '/packages/management/options',
  );

  return {
    templates: (res.data.data.templates ?? []).map((item) => ({
      value: asNumber((item as Record<string, unknown>).value),
      label: asString((item as Record<string, unknown>).label),
    })),
    facilities: (res.data.data.facilities ?? []).map((item) => ({
      value: asNumber((item as Record<string, unknown>).value),
      label: asString((item as Record<string, unknown>).label),
    })),
  };
}

export async function getPackageManagementRows(
  filters: PackageManagementFilters,
): Promise<PaginatedResult<PackageManagementRow, PackageManagementStats>> {
  const res = await apiClient.get<{ data: PaginatedResult<Record<string, unknown>, Record<string, unknown>> }>(
    '/packages/management',
    { params: cleanParams(filters) },
  );

  const payload = res.data.data;

  return {
    items: (payload.items ?? []).map((row) => ({
      patientPackageId: asNumber(row.patientPackageId),
      packageId: asNumber(row.packageId),
      patientId: asNumber(row.patientId),
      paymentId: row.paymentId == null ? null : asNumber(row.paymentId),
      planId: row.planId == null ? null : asNumber(row.planId),
      patientName: asString(row.patientName),
      type: asString(row.type),
      amount: asNumber(row.amount),
      status: asString(row.status, 'active'),
      productsRemaining: asNumber(row.productsRemaining, 0),
      notes: asNullableString(row.notes),
      facilityId: row.facilityId == null ? null : asNumber(row.facilityId),
      userName: asNullableString(row.userName),
      createdAt: asNullableString(row.createdAt),
    })),
    pagination: {
      page: asNumber(payload.pagination?.page, 1),
      perPage: asNumber(payload.pagination?.perPage, filters.perPage),
      total: asNumber(payload.pagination?.total, 0),
    },
    sort: {
      field: asString(payload.sort?.field, filters.sortBy),
      direction: asString(payload.sort?.direction, filters.sortDir) === 'asc' ? 'asc' : 'desc',
    },
    stats: {
      activeCount: asNumber(payload.stats?.activeCount),
      declinedCount: asNumber(payload.stats?.declinedCount),
      inactiveCount: asNumber(payload.stats?.inactiveCount),
    },
  };
}
